/**
 * Client-side mirror of the API's `DEFAULT_TRACKING_PAGE_CONFIG`
 * (api/app/util/TrackingPageConfig.ts).
 *
 * The server merges the stored blob over its own defaults before returning it,
 * so this copy exists only to seed the editor for a store that has never saved
 * settings. Keep the two in sync when adding a field.
 */

export type TrackingPageFont = 'system' | 'hanken' | 'serif' | 'mono';
export type TrackingPageCornerRadius = 'sharp' | 'rounded' | 'pill';
export type TrackingPageCoverPosition = 'left' | 'right';

export type TrackingPageAdPlacement =
  | 'above_timeline'
  | 'below_timeline'
  | 'below_items';

export type TrackingPageAd = {
  id: string;
  /** Shopify product this snapshot came from, when picked rather than hand-built. */
  productId: string | null;
  imageUrl: string;
  headline: string | null;
  body: string | null;
  ctaLabel: string | null;
  ctaUrl: string;
  placement: TrackingPageAdPlacement;
};

export type TrackingPageConfig = {
  version: number;
  theme: {
    primaryColor: string;
    backgroundColor: string;
    surfaceColor: string;
    textColor: string;
    fontFamily: TrackingPageFont;
    cornerRadius: TrackingPageCornerRadius;
  };
  branding: {
    logoUrl: string | null;
    logoHeight: number;
    coverImageUrl: string | null;
    coverImagePosition: TrackingPageCoverPosition;
    showPoweredBy: boolean;
  };
  sections: {
    hero: boolean;
    progress: boolean;
    timeline: boolean;
    items: boolean;
    ads: boolean;
    support: boolean;
  };
  copy: {
    headline: string | null;
    supportEmail: string | null;
    supportUrl: string | null;
  };
  ads: TrackingPageAd[];
};

export const DEFAULT_TRACKING_PAGE_CONFIG: TrackingPageConfig = {
  version: 1,
  theme: {
    primaryColor: '#1565c0',
    backgroundColor: '#f6f7fb',
    surfaceColor: '#ffffff',
    textColor: '#0b1c30',
    fontFamily: 'system',
    cornerRadius: 'rounded',
  },
  branding: {
    logoUrl: null,
    logoHeight: 48,
    coverImageUrl: null,
    coverImagePosition: 'left',
    showPoweredBy: true,
  },
  sections: {
    hero: true,
    progress: true,
    timeline: true,
    items: true,
    ads: false,
    support: true,
  },
  copy: { headline: null, supportEmail: null, supportUrl: null },
  ads: [],
};

// Only families the tracking portal can actually render: HankRnd is self-hosted
// there, the rest are OS-guaranteed. A family without shipped font files falls
// back to the system sans and becomes a duplicate of the "System" option.
export const FONT_OPTIONS: { label: string; value: TrackingPageFont }[] = [
  { label: 'System (default)', value: 'system' },
  { label: 'Hanken', value: 'hanken' },
  { label: 'Serif', value: 'serif' },
  { label: 'Monospace', value: 'mono' },
];

export const CORNER_OPTIONS: {
  label: string;
  value: TrackingPageCornerRadius;
}[] = [
  { label: 'Square', value: 'sharp' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Pill', value: 'pill' },
];

export const COVER_POSITION_OPTIONS: {
  label: string;
  value: TrackingPageCoverPosition;
}[] = [
  { label: 'Left of the search box', value: 'left' },
  { label: 'Right of the search box', value: 'right' },
];

export const AD_PLACEMENT_OPTIONS: {
  label: string;
  value: TrackingPageAdPlacement;
}[] = [
  { label: 'Above tracking history', value: 'above_timeline' },
  { label: 'Below tracking history', value: 'below_timeline' },
  { label: 'Below items', value: 'below_items' },
];

export const PREVIEW_STATUSES = [
  'PRE_TRANSIT',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'AVAILABLE_FOR_PICKUP',
  'FAILURE',
  'ERROR',
  'RETURN_TO_SENDER',
  'CANCELLED',
  'UNKNOWN',
] as const;

/** Merge a stored blob over the defaults so a partial config still renders. */
export function withDefaults(stored: any): TrackingPageConfig {
  const source = stored && typeof stored === 'object' ? stored : {};
  const d = DEFAULT_TRACKING_PAGE_CONFIG;

  return {
    version: d.version,
    theme: { ...d.theme, ...(source.theme || {}) },
    branding: { ...d.branding, ...(source.branding || {}) },
    sections: { ...d.sections, ...(source.sections || {}) },
    copy: { ...d.copy, ...(source.copy || {}) },
    ads: Array.isArray(source.ads) ? source.ads : [],
  };
}
